import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";

const EMPTY = {
  propertyId: "",
  customerId: "",
  payerName: "",
  payerEmail: "",
  payerDocument: "",
  payerPhone: "",
  startDate: "",
  endDate: "",
  rentAmount: "",
  currency: "ARS",
  dueDay: 10,
  status: "draft",
  depositAmount: "",
  indexationEnabled: false,
  indexationFrequencyMonths: "",
  indexationIndexName: "",
  indexationNotes: "",
  collectionsEnabled: true,
  autoGenerateRent: true,
  generateMonthsAhead: 1,
  reminderDaysBefore: "5, 2",
  reminderOnDueDate: true,
  reminderDaysAfter: "3, 7",
  sendPaymentConfirmation: true,
};

function dateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function toForm(lease) {
  if (!lease) return EMPTY;
  return {
    ...EMPTY,
    propertyId: String(lease.propertyId?._id || lease.propertyId || ""),
    customerId: String(lease.customerId?._id || lease.customerId || ""),
    payerName: lease.payer?.name || "",
    payerEmail: lease.payer?.email || "",
    payerDocument: lease.payer?.document || "",
    payerPhone: lease.payer?.phone || "",
    startDate: dateInput(lease.startDate),
    endDate: dateInput(lease.endDate),
    rentAmount: lease.rentAmount ?? "",
    currency: lease.currency || "ARS",
    dueDay: lease.dueDay ?? 10,
    status: lease.status || "draft",
    depositAmount: lease.deposit?.amount ?? "",
    indexationEnabled: lease.indexation?.enabled === true,
    indexationFrequencyMonths: lease.indexation?.frequencyMonths ?? "",
    indexationIndexName: lease.indexation?.indexName || "",
    indexationNotes: lease.indexation?.notes || "",
    collectionsEnabled: lease.collectionsAutomation?.enabled !== false,
    autoGenerateRent: lease.collectionsAutomation?.autoGenerateRent !== false,
    generateMonthsAhead: lease.collectionsAutomation?.generateMonthsAhead ?? 1,
    reminderDaysBefore: Array.isArray(lease.collectionsAutomation?.reminderDaysBefore)
      ? lease.collectionsAutomation.reminderDaysBefore.join(", ")
      : "5, 2",
    reminderOnDueDate: lease.collectionsAutomation?.reminderOnDueDate !== false,
    reminderDaysAfter: Array.isArray(lease.collectionsAutomation?.reminderDaysAfter)
      ? lease.collectionsAutomation.reminderDaysAfter.join(", ")
      : "3, 7",
    sendPaymentConfirmation: lease.collectionsAutomation?.sendPaymentConfirmation !== false,
  };
}

function parseDays(value, max = 120) {
  return [...new Set(String(value || "")
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item >= 0 && item <= max))]
    .sort((a, b) => a - b);
}

export default function LeaseDialog({ open, lease, properties = [], customers = [], customersUnavailable = false, onClose, onSave, saving = false }) {
  const [form, setForm] = React.useState(EMPTY);
  React.useEffect(() => { if (open) setForm(toForm(lease)); }, [open, lease]);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const hasInvalidDateRange = Boolean(form.startDate && form.endDate && form.endDate < form.startDate);

  const submit = async (event) => {
    event.preventDefault();
    if (hasInvalidDateRange) return;
    const payload = {
      propertyId: form.propertyId,
      payer: {
        name: form.payerName.trim(),
        email: form.payerEmail.trim(),
        document: form.payerDocument.trim(),
        phone: form.payerPhone.trim(),
      },
      startDate: form.startDate,
      endDate: form.endDate,
      rentAmount: Number(form.rentAmount),
      currency: form.currency.trim().toUpperCase(),
      dueDay: Number(form.dueDay),
      status: form.status,
      deposit: { amount: form.depositAmount === "" ? 0 : Number(form.depositAmount), currency: form.currency.trim().toUpperCase() },
      indexation: {
        enabled: Boolean(form.indexationEnabled),
        frequencyMonths: form.indexationFrequencyMonths === "" ? 0 : Number(form.indexationFrequencyMonths),
        indexName: form.indexationIndexName.trim(),
        notes: form.indexationNotes.trim(),
      },
      collectionsAutomation: {
        enabled: Boolean(form.collectionsEnabled),
        autoGenerateRent: Boolean(form.autoGenerateRent),
        generateMonthsAhead: Number(form.generateMonthsAhead || 0),
        reminderDaysBefore: parseDays(form.reminderDaysBefore, 60),
        reminderOnDueDate: Boolean(form.reminderOnDueDate),
        reminderDaysAfter: parseDays(form.reminderDaysAfter),
        sendPaymentConfirmation: Boolean(form.sendPaymentConfirmation),
      },
    };
    if (!customersUnavailable || !lease) payload.customerId = form.customerId || null;
    await onSave(payload);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 950 }}>{lease ? "Editar contrato" : "Nuevo contrato de alquiler"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}><TextField select fullWidth required label="Propiedad" value={form.propertyId} onChange={set("propertyId")}><MenuItem value="">Seleccionar</MenuItem>{properties.map((property) => <MenuItem key={property._id || property.id} value={property._id || property.id}>{property.code ? `${property.code} · ` : ""}{property.title}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} sm={5}><TextField select fullWidth label="Cliente vinculado" value={form.customerId} onChange={set("customerId")} disabled={customersUnavailable} error={customersUnavailable} helperText={customersUnavailable ? "No pudimos cargar los clientes. Podés completar los datos del pagador manualmente." : undefined}><MenuItem value="">Sin cliente vinculado</MenuItem>{customersUnavailable && form.customerId ? <MenuItem value={form.customerId}>Cliente vinculado actual</MenuItem> : null}{customers.map((customer) => <MenuItem key={customer._id || customer.id} value={customer._id || customer.id}>{customer.name || customer.nombre || customer.email || customer._id}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12}><Box sx={{ fontWeight: 900, color: "var(--wp-panel-text-primary, #19242F)", mt: 1 }}>Datos del pagador</Box></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre" value={form.payerName} onChange={set("payerName")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="email" label="Email" value={form.payerEmail} onChange={set("payerEmail")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Documento" value={form.payerDocument} onChange={set("payerDocument")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono" value={form.payerPhone} onChange={set("payerPhone")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth required type="date" label="Inicio" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={set("startDate")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth required type="date" label="Fin" InputLabelProps={{ shrink: true }} inputProps={form.startDate ? { min: form.startDate } : undefined} value={form.endDate} onChange={set("endDate")} error={hasInvalidDateRange} helperText={hasInvalidDateRange ? "La fecha de fin no puede ser anterior a la fecha de inicio." : undefined} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth required type="number" inputProps={{ min: 0, step: "any" }} label="Alquiler" value={form.rentAmount} onChange={set("rentAmount")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Moneda" inputProps={{ maxLength: 3 }} value={form.currency} onChange={set("currency")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth required type="number" inputProps={{ min: 1, max: 31 }} label="Día de vencimiento" value={form.dueDay} onChange={set("dueDay")} /></Grid>
            <Grid item xs={6} sm={3}><TextField select fullWidth label="Estado" value={form.status} onChange={set("status")}><MenuItem value="draft">Borrador</MenuItem><MenuItem value="active">Activo</MenuItem><MenuItem value="ended">Finalizado</MenuItem><MenuItem value="cancelled">Cancelado</MenuItem></TextField></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" inputProps={{ min: 0, step: "any" }} label="Depósito" value={form.depositAmount} onChange={set("depositAmount")} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Checkbox checked={form.indexationEnabled} onChange={(event) => setForm((current) => ({ ...current, indexationEnabled: event.target.checked }))} />} label="Aplicar configuración de indexación" /></Grid>
            {form.indexationEnabled ? <>
              <Grid item xs={12} sm={4}><TextField fullWidth type="number" inputProps={{ min: 0, max: 120 }} label="Frecuencia (meses)" value={form.indexationFrequencyMonths} onChange={set("indexationFrequencyMonths")} /></Grid>
              <Grid item xs={12} sm={8}><TextField fullWidth label="Índice" value={form.indexationIndexName} onChange={set("indexationIndexName")} placeholder="ICL, IPC u otro" /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Notas de indexación" value={form.indexationNotes} onChange={set("indexationNotes")} /></Grid>
            </> : null}
            <Grid item xs={12}><Box sx={{ fontWeight: 900, color: "var(--wp-panel-text-primary, #19242F)", mt: 1 }}>Automatización de cobranzas</Box></Grid>
            <Grid item xs={12}><FormControlLabel control={<Checkbox checked={form.collectionsEnabled} onChange={(event) => setForm((current) => ({ ...current, collectionsEnabled: event.target.checked }))} />} label="Activar generación y recordatorios automáticos" /></Grid>
            {form.collectionsEnabled ? <>
              <Grid item xs={12} sm={6}><FormControlLabel control={<Checkbox checked={form.autoGenerateRent} onChange={(event) => setForm((current) => ({ ...current, autoGenerateRent: event.target.checked }))} />} label="Generar obligaciones de alquiler" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="number" inputProps={{ min: 0, max: 12 }} label="Meses a generar por adelantado" value={form.generateMonthsAhead} onChange={set("generateMonthsAhead")} helperText="0 genera sólo el período actual; máximo 12." /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Recordar días antes" value={form.reminderDaysBefore} onChange={set("reminderDaysBefore")} helperText="Separados por coma. Ej.: 5, 2" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Recordar días después" value={form.reminderDaysAfter} onChange={set("reminderDaysAfter")} helperText="Separados por coma. Ej.: 3, 7" /></Grid>
              <Grid item xs={12} sm={6}><FormControlLabel control={<Checkbox checked={form.reminderOnDueDate} onChange={(event) => setForm((current) => ({ ...current, reminderOnDueDate: event.target.checked }))} />} label="Enviar aviso el día del vencimiento" /></Grid>
              <Grid item xs={12} sm={6}><FormControlLabel control={<Checkbox checked={form.sendPaymentConfirmation} onChange={(event) => setForm((current) => ({ ...current, sendPaymentConfirmation: event.target.checked }))} />} label="Enviar confirmación cuando se acredita el pago" /></Grid>
            </> : null}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button type="submit" variant="contained" disabled={saving || !form.propertyId || !form.startDate || !form.endDate || form.rentAmount === "" || hasInvalidDateRange}>{saving ? "Guardando..." : "Guardar"}</Button></DialogActions>
      </Box>
    </Dialog>
  );
}
